// src/Redux/Projects/DailyLog/dailyLogSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosWithAuth from '../UtilsRedux/axiosWithAuth';
import { API_ROUTES } from '../Constants/apiRoutes';
import { uploadToFirebase } from '../UtilsRedux/uploadToFirebase';
import { showFeedback } from '../UI/uiFeedbackSlice';

/* ------------------------- Helpers ------------------------- */
const buildQS = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    q.set(k, Array.isArray(v) ? v.join(',') : v);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

/* ------------------------- Thunks -------------------------- */

/**
 * GET /daily-logs/project/:projectId
 * params supports: type, from, to, hasImage, search, sort(new|old), page, limit
 */
export const fetchDailyLogs = createAsyncThunk(
  'dailyLogs/fetchByProject',
  async ({ projectId, params = {} }, { rejectWithValue, dispatch }) => {
    try {
      const qs = buildQS(params);
      const res = await axiosWithAuth.get(API_ROUTES.GET_DAILY_LOGS(projectId, qs.replace(/^\?/, '')));
      return res.data; // { success, data:[], meta:{} }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch logs';

      dispatch(
        showFeedback({
          type: 'warning',
          title: 'Could not load daily logs',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

/**
 * POST /daily-logs
 * payload: { projectId, type, message, metadata?, imageFile?, imageUrl?, logDate? }
 * - If imageFile provided, it will be uploaded to Firebase first
 */
export const createDailyLog = createAsyncThunk(
  'dailyLogs/create',
  async ({ projectId, type, message, metadata, imageFile, imageUrl, logDate, uid }, { rejectWithValue, dispatch }) => {
    try {
      let finalImageUrl = imageUrl || null;
      if (!finalImageUrl && imageFile) {
        // Path: dailyLogs/<uid || anon>/<projectId>/
        const prefix = `dailyLogs/${uid || 'anon'}/${projectId}`;
        finalImageUrl = await uploadToFirebase(imageFile, prefix);
      }

      const body = {
        projectId,
        type,
        message,
        ...(metadata ? { metadata } : {}),
        ...(finalImageUrl ? { imageUrl: finalImageUrl } : {}),
        ...(logDate ? { logDate } : {}),
      };

      const res = await axiosWithAuth.post(API_ROUTES.CREATE_DAILY_LOG, body);
      // Return the created document
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Daily log added',
          message: 'Your update has been saved.',
        })
      );

      return res.data.data; // created doc
    } catch (err) {
       const message =
        err?.response?.data?.message || 'Failed to create log';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Daily log failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);



/** DELETE /daily-logs/:id */
export const deleteDailyLog = createAsyncThunk(
  'dailyLogs/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axiosWithAuth.delete(API_ROUTES.DELETE_DAILY_LOG(id));
       dispatch(
        showFeedback({
          type: 'success',
          title: 'Daily log deleted',
          message: 'The log has been removed.',
        })
      );

      return id;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to delete log';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Delete failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

/* ------------------------- Slice --------------------------- */
const initialState = {
  list: [],          // current page of logs
  meta: {            // pagination meta from backend
    total: 0, page: 1, limit: 20, pages: 1, sort: 'new',
  },
  status: 'idle',    // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,

  creating: 'idle',  // 'idle' | 'loading' | 'succeeded' | 'failed'
  createError: null,
  deleting: {},
};

const dailyLogSlice = createSlice({
  name: 'dailyLogs',
   initialState,
  reducers: {
    // ✅ fixed: single, correct implementation
    resetDailyLogs: () => ({
      ...initialState,
      deleting: {},
    }),
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchDailyLogs.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDailyLogs.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload.data || [];
        state.meta = action.payload.meta || initialState.meta;
      })
      .addCase(fetchDailyLogs.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // create
      .addCase(createDailyLog.pending, (state) => {
        state.creating = 'loading';
        state.createError = null;
      })
      .addCase(createDailyLog.fulfilled, (state, action) => {
        state.creating = 'succeeded';
        // Optimistically put the new log at the top when sort=new
        if (state.meta?.sort !== 'old') {
          state.list.unshift(action.payload);
        } else {
          state.list.push(action.payload);
        }
        state.meta.total += 1;
      })
      .addCase(createDailyLog.rejected, (state, action) => {
        state.creating = 'failed';
        state.createError = action.payload;
      })


      // delete
      .addCase(deleteDailyLog.pending, (state, action) => {
        const id = action.meta.arg;
        state.deleting[id] = 'loading';
      })
      .addCase(deleteDailyLog.fulfilled, (state, action) => {
        const id = action.payload;
        delete state.deleting[id];
        state.list = state.list.filter(
          (x) => (x._id || x.id) !== id
        );
        if (state.meta && typeof state.meta.total === 'number') {
          state.meta.total = Math.max(0, state.meta.total - 1);
        }
      })
      .addCase(deleteDailyLog.rejected, (state, action) => {
        const id = action.meta.arg;
        delete state.deleting[id];
        state.error = action.payload;
      });
  },
});

export const { resetDailyLogs } = dailyLogSlice.actions;
export default dailyLogSlice.reducer;

/* ------------------------- Selectors ----------------------- */
export const selectDailyLogs = (state) => state.dailyLogs.list;
export const selectDailyLogsMeta = (state) => state.dailyLogs.meta;
export const selectDailyLogsStatus = (state) => state.dailyLogs.status;
export const selectCreateLogStatus = (state) => state.dailyLogs.creating;
export const selectDeletingMap = (s) => s.dailyLogs.deleting;